import { MODEL_CATALOG, PROVIDER_DEFINITIONS } from './catalog.js';
import type {
  CostTier,
  ModelCapability,
  ModelEntry,
  ModelPricing,
  ProviderDefinition,
  ProviderId,
} from './types.js';

const DEFAULT_MODEL_PRICING: ModelPricing = {
  inputPerMillion: 3,
  outputPerMillion: 15,
};

const normalizeModelKey = (value: string): string => value.trim().toLowerCase();

const exactIdIndex = new Map<string, ModelEntry>();
const aliasIndex = new Map<string, ModelEntry[]>();

for (const entry of MODEL_CATALOG) {
  exactIdIndex.set(normalizeModelKey(entry.id), entry);
  for (const alias of entry.aliases) {
    const key = normalizeModelKey(alias);
    const existing = aliasIndex.get(key);
    if (existing) {
      existing.push(entry);
    } else {
      aliasIndex.set(key, [entry]);
    }
  }
}

const sortAliasMatches = (entries: ModelEntry[]): ModelEntry[] =>
  [...entries].sort((left, right) => {
    if (left.status !== right.status) {
      return left.status === 'deprecated' ? 1 : -1;
    }
    return MODEL_CATALOG.indexOf(left) - MODEL_CATALOG.indexOf(right);
  });

const pickProviderMatch = (
  matches: ModelEntry[],
  preferredProvider?: ProviderId,
): ModelEntry | undefined => {
  if (!preferredProvider) {
    return sortAliasMatches(matches)[0];
  }

  const directMatch = sortAliasMatches(matches).find(
    (entry) => entry.provider === preferredProvider,
  );
  return directMatch ?? sortAliasMatches(matches)[0];
};

const isZeroCostModel = (modelId: string): boolean =>
  modelId.trim().toLowerCase().endsWith(':free');

const titleCaseToken = (value: string): string =>
  value
    .split(/[-_]/g)
    .filter(Boolean)
    .map((token) =>
      /^\d+(\.\d+)?$/.test(token) ? token : token.charAt(0).toUpperCase() + token.slice(1),
    )
    .join(' ');

export const getProviderDefinition = (provider: ProviderId): ProviderDefinition => {
  const definition = PROVIDER_DEFINITIONS[provider];
  if (!definition) {
    throw new Error(`Unknown provider: ${provider}`);
  }
  return definition;
};

export const getModel = (
  idOrAlias: string,
  preferredProvider?: ProviderId,
): ModelEntry | undefined => {
  const key = normalizeModelKey(idOrAlias);
  if (!key) {
    return undefined;
  }

  const exact = exactIdIndex.get(key);
  if (exact && (!preferredProvider || exact.provider === preferredProvider)) {
    return exact;
  }

  const aliasMatches = aliasIndex.get(key) ?? [];
  if (aliasMatches.length > 0) {
    return pickProviderMatch(aliasMatches, preferredProvider) ?? exact;
  }

  return exact;
};

export const getModelsForProvider = (
  provider: ProviderId,
  options: { includeDeprecated?: boolean } = {},
): ModelEntry[] =>
  MODEL_CATALOG.filter(
    (entry) =>
      entry.provider === provider && (options.includeDeprecated || entry.status !== 'deprecated'),
  );

export const getModelsWithCapability = (
  capability: ModelCapability,
  options: { includeDeprecated?: boolean } = {},
): ModelEntry[] =>
  MODEL_CATALOG.filter(
    (entry) =>
      entry.capabilities.includes(capability) &&
      (options.includeDeprecated || entry.status !== 'deprecated'),
  );

export const getModelPricing = (
  idOrAlias: string,
  preferredProvider?: ProviderId,
): ModelPricing => {
  if (isZeroCostModel(idOrAlias)) {
    return { inputPerMillion: 0, outputPerMillion: 0 };
  }

  return getModel(idOrAlias, preferredProvider)?.pricing ?? DEFAULT_MODEL_PRICING;
};

export const getModelDisplayName = (idOrAlias: string, preferredProvider?: ProviderId): string => {
  const model = getModel(idOrAlias, preferredProvider);
  if (model) {
    return model.displayName;
  }

  const trimmed = idOrAlias.trim();
  if (!trimmed) {
    return 'Unknown Model';
  }

  const withoutFreeSuffix = trimmed.replace(/:free$/i, '');
  const finalSegment =
    withoutFreeSuffix.split('/').at(-1)?.replace(/\./g, '-') ?? withoutFreeSuffix;
  return titleCaseToken(finalSegment);
};

export const resolveModelId = (idOrAlias: string, preferredProvider?: ProviderId): string => {
  const resolved = getModel(idOrAlias, preferredProvider);
  return resolved?.id ?? idOrAlias.trim();
};

export const isKnownModel = (idOrAlias: string, preferredProvider?: ProviderId): boolean =>
  Boolean(getModel(idOrAlias, preferredProvider));

export const getModelCostTier = (idOrAlias: string, preferredProvider?: ProviderId): CostTier => {
  if (isZeroCostModel(idOrAlias)) {
    return 'free';
  }

  return getModel(idOrAlias, preferredProvider)?.costTier ?? 'medium';
};

export const getModelContextLength = (
  idOrAlias: string,
  preferredProvider?: ProviderId,
): number | null => getModel(idOrAlias, preferredProvider)?.contextLength ?? null;

export const validateModelForProvider = (modelId: string, provider: ProviderId): boolean => {
  const trimmed = modelId.trim();
  if (!trimmed) {
    return false;
  }

  const matched = getModel(trimmed, provider);
  if (matched?.provider === provider) {
    return true;
  }

  return getProviderDefinition(provider).idPattern.test(trimmed);
};
